import {
  DEFAULT_LOCALE,
  LOCALES,
  LOCALE_CODES,
  ROUTE_NAME_SEPARATOR,
  STAFF_PATH_PREFIXES,
  type LocaleCode,
} from '~/constants/i18n';

export type LocalePrefs = {
  userLanguageIso?: string | null;
  cookie?: string | null;
  browserLanguages: readonly string[];
};

export type DecideLocaleInput = {
  path: string;
  routeName: unknown;
  prefs: LocalePrefs;
  enabled: readonly LocaleCode[];
};

// `persist`: the locale came from an explicit non-English URL prefix (e.g. landing on `/he/`), so
// the client stores it in the locale cookie — unprefixed pages (auth/signup, portal) then follow it.
export type LocaleAction = { type: 'set'; locale: LocaleCode; persist?: true } | { type: 'redirect'; to: string };

export const isLocaleCode = (value: unknown): value is LocaleCode =>
  typeof value === 'string' && (LOCALE_CODES as readonly string[]).includes(value);

export const normalizeLocale = (raw: string | null | undefined): LocaleCode | null => {
  const base = raw?.trim().toLowerCase().split(/[-_]/)[0];
  return isLocaleCode(base) ? base : null;
};

export const parseEnabledLocales = (raw: string | null | undefined): LocaleCode[] => {
  const parsed = (raw ?? '')
    .split(',')
    .map(normalizeLocale)
    .filter((c): c is LocaleCode => c !== null);
  return [...new Set<LocaleCode>([DEFAULT_LOCALE, ...parsed])];
};

/** Enabled locales from a runtime config (`public.enabledLocales`, NUXT_PUBLIC_ENABLED_LOCALES); always includes `en`. */
export const enabledLocalesFromConfig = (config: { public?: { enabledLocales?: unknown } }): LocaleCode[] =>
  parseEnabledLocales(String(config.public?.enabledLocales ?? ''));

/**
 * The locale to actually serve for `raw` (a user's `languageIso`, a cookie, a client-sent code…):
 * the normalized code when it is enabled, otherwise English — a disabled or unknown locale never
 * leaks into emails, SMS, AI answers or stored preferences.
 */
export const effectiveLocale = (raw: unknown, enabled: readonly LocaleCode[]): LocaleCode => {
  const code = typeof raw === 'string' ? normalizeLocale(raw) : null;
  return code && enabled.includes(code) ? code : DEFAULT_LOCALE;
};

export const getLocaleMeta = (code: LocaleCode) => LOCALES.find((l) => l.code === code) ?? LOCALES[0];

export const resolvePreferredLocale = (
  prefs: LocalePrefs,
  enabled: readonly LocaleCode[],
  { includeBrowser = true }: { includeBrowser?: boolean } = {},
): LocaleCode => {
  const candidates = [prefs.userLanguageIso, prefs.cookie, ...(includeBrowser ? prefs.browserLanguages : [])];
  for (const candidate of candidates) {
    const code = normalizeLocale(candidate);
    if (code && enabled.includes(code)) return code;
  }
  return DEFAULT_LOCALE;
};

export const localeFromRouteName = (name: unknown): LocaleCode | null => {
  if (typeof name !== 'string' || !name.includes(ROUTE_NAME_SEPARATOR)) return null;
  const suffix = name.split(ROUTE_NAME_SEPARATOR).pop();
  return isLocaleCode(suffix) ? suffix : null;
};

export const isStaffPath = (path: string): boolean => {
  const pathWithoutQuery = path.split(/[?#]/)[0];
  return STAFF_PATH_PREFIXES.some((prefix) => pathWithoutQuery === prefix || pathWithoutQuery.startsWith(`${prefix}/`));
};

const stripPrefix = (path: string, locale: LocaleCode): string => {
  if (locale === DEFAULT_LOCALE) return path;
  const prefix = `/${locale}`;
  if (path === prefix) return '/';
  if (path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`) || path.startsWith(`${prefix}#`)) {
    const rest = path.slice(prefix.length);
    return rest.startsWith('?') || rest.startsWith('#') ? `/${rest}` : rest;
  }
  return path;
};

/**
 * Guarantees a same-origin, root-relative path. Collapses any leading run of `/`, `\`, ASCII
 * whitespace, and control characters into a single `/`, then ensures the result starts with `/`.
 * `\` is included because browsers treat it as `/`; whitespace/control characters are included
 * because browsers strip ASCII tab/CR/LF anywhere in a URL, so `/\t/evil.com` becomes `//evil.com`
 * by the time it reaches the address bar. Only the leading run is touched — whitespace elsewhere
 * in the path is left alone. Never decodes: an already percent-encoded `%2F`/`%5C` is left as-is,
 * since browsers treat it as literal path characters rather than a separator.
 */
export const toSafeLocalPath = (path: string): string => {
  const collapsed = path.replace(/^[\s/\\\x00-\x1F\x7F]+/, '/');
  return collapsed.startsWith('/') ? collapsed : `/${collapsed}`;
};

export const localizePath = (path: string, from: LocaleCode, to: LocaleCode): string => {
  const base = stripPrefix(path, from);
  if (to === DEFAULT_LOCALE) return toSafeLocalPath(base);
  if (base === '/') return toSafeLocalPath(`/${to}`);
  if (base.startsWith('/?') || base.startsWith('/#')) return toSafeLocalPath(`/${to}${base.slice(1)}`);
  return toSafeLocalPath(`/${to}${base}`);
};

const localePrefixOf = (path: string): LocaleCode | null => {
  const match = /^\/([a-z]{2})(?=$|[/?#])/.exec(path);
  const code = match?.[1];
  return isLocaleCode(code) && code !== DEFAULT_LOCALE ? code : null;
};

/** Removes a leading `/<locale>` segment (any non-English locale) so path checks can match literal public paths. */
export const stripLocalePrefix = (path: string): string => {
  const code = localePrefixOf(path);
  return code ? stripPrefix(path, code) : path;
};

/**
 * Server-side, cache-safe redirect for URLs prefixed with a locale that is not enabled.
 * Decides from the URL and the enabled list only (identical for every visitor).
 */
export const disabledLocaleRedirect = (pathWithQuery: string, enabled: readonly LocaleCode[]): string | null => {
  const code = localePrefixOf(pathWithQuery);
  if (!code || enabled.includes(code)) return null;
  return toSafeLocalPath(stripPrefix(pathWithQuery, code));
};

// Anything encodeURI would escape, plus a `%` that doesn't start a valid escape. Existing `%XX`
// escapes are kept as-is (never double-encoded).
const NEEDS_URI_ENCODING = /%(?![0-9A-Fa-f]{2})|[^\w\-.!~*'();/?:@&=+$,#%[\]]/gu;
const REPLACEMENT_CHARACTER_UTF8 = '%EF%BF%BD';

const encodeUriChar = (char: string): string => {
  try {
    return encodeURI(char);
  } catch {
    return REPLACEMENT_CHARACTER_UTF8; // lone surrogate
  }
};

/**
 * `disabledLocaleRedirect` for h3 middleware, returning a Location Node accepts. h3's `event.path`
 * has the path part percent-decoded (non-ASCII there makes `setHeader('location')` throw → 500) and
 * the query raw; `rawUrl` is the request target as received and tells where the real query starts,
 * so a decoded `%3F`/`%23` stays a literal path character instead of becoming a query/hash.
 * Re-encoding happens after `toSafeLocalPath`, so the same-origin collapse still sees decoded `/`,
 * `\` and control characters.
 */
export const disabledLocaleRedirectLocation = (
  decodedPath: string,
  rawUrl: string,
  enabled: readonly LocaleCode[],
): string | null => {
  const queryStart = rawUrl.indexOf('?');
  const rawQuery = queryStart === -1 ? '' : rawUrl.slice(queryStart);
  const pathPart = rawQuery && decodedPath.endsWith(rawQuery) ? decodedPath.slice(0, -rawQuery.length) : decodedPath;
  const literalPath = pathPart.replace(/[?#]/g, (char) => encodeURIComponent(char));
  const target = disabledLocaleRedirect(`${literalPath}${rawQuery}`, enabled);
  return target && target.replace(NEEDS_URI_ENCODING, encodeUriChar);
};

export const decideLocaleAction = ({ path, routeName, prefs, enabled }: DecideLocaleInput): LocaleAction => {
  if (isStaffPath(path)) return { type: 'set', locale: DEFAULT_LOCALE };

  const routeLocale = localeFromRouteName(routeName);
  if (!routeLocale) return { type: 'set', locale: resolvePreferredLocale(prefs, enabled) };

  if (!enabled.includes(routeLocale)) return { type: 'redirect', to: localizePath(path, routeLocale, DEFAULT_LOCALE) };

  if (routeLocale === DEFAULT_LOCALE) {
    // Only explicit choices (profile/cookie) move a visitor off the English URL — never the
    // browser header, so crawlers and first-time visitors see the URL's language.
    const explicit = resolvePreferredLocale(prefs, enabled, { includeBrowser: false });
    if (explicit !== DEFAULT_LOCALE) return { type: 'redirect', to: localizePath(path, DEFAULT_LOCALE, explicit) };
  }

  return routeLocale === DEFAULT_LOCALE
    ? { type: 'set', locale: routeLocale }
    : { type: 'set', locale: routeLocale, persist: true };
};
